<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="items.items"
      item-key="id"
      class="background-transparent"
      :items-per-page="25"
      :loading="loading"
      :options.sync="options"
      :server-items-length="items.meta.totalItems"
      :footer-props="{
        'items-per-page-options': rowsPerPageItemsGlobal
      }"
    >
      <template v-slot:top>
        <div class="align-center pr-3 mb-5" style="width: 100%">
          <div class="d-inline-flex align-center justify-space-between max-width">
            <div class="d-flex align-center">
              <v-switch
                v-model="includeSystem"
                data-cy="switch-include-system"
                :label="$lang.labels.includeSystem"
                class="pl-2 pt-1"
                inset
                dense
              ></v-switch>
              <v-switch
                v-model="activeOnly"
                data-cy="switch-active-only"
                :label="$lang.labels.activeOnly"
                class="pl-3 pt-1 pr-3"
                inset
                dense
              ></v-switch>
              <v-text-field
                v-model="search"
                class="custom-search-perm"
                outlined
                hide-details
                clearable
                dense
                :label="$lang.labels.searchByName"
                data-cy="search-by-name"
                style="max-width: 340px"
              ></v-text-field>
              <v-select
                v-model="permissionType"
                class="ml-2 custom-search-perm"
                hide-details
                :items="typesFormatted"
                :label="$lang.labels.permissionType"
                outlined
                dense
                clearable
                style="max-width: 340px"
              ></v-select>
            </div>
            <div class="d-flex">
              <div>
                <v-btn
                  text
                  light
                  class="ml-2"
                  color="primary"
                  @click="$emit('fetchProcesses', { options: options, search: search && search.length > 1 ? search : '', permissionType: permissionType, resetPage: true })"
                >
                  <v-icon
                    dense
                    small
                  >
                    mdi-refresh
                  </v-icon>
                </v-btn>
              </div>
              <div class="custom-button-wrapper">
                <v-btn
                  v-if="(roles.includes('PROCESS_CREATOR') || roles.includes('SUPER_USER')) && notChromeBased"
                  outlined
                  color="secondary"
                  class="ml-2"
                  @click="createFromClipboard()"
                >
                  <v-icon
                    left
                    dark
                  >
                    mdi-memory
                  </v-icon>
                  {{ $lang.actions.createFromMemory }}</v-btn>
                <v-btn
                  v-if="roles.includes('PROCESS_CREATOR') || roles.includes('SUPER_USER')"
                  outlined
                  color="accent"
                  class=" ml-2 color-accent-text"
                  :to="{ name: 'processCreate' }"
                >
                  <v-icon
                    left
                    dark
                  >
                    mdi-file-plus
                  </v-icon>
                  {{ $lang.actions.create }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:item.id="{ item }">
        <div class="font-weight-bold"># {{ item.id }}</div>
      </template>

      <template v-slot:item.name="{ item }">
        <div class="d-flex justify-space-between align-center">
          <!-- Name on the left with a fixed or max width -->
          <div class="text-start name-container clickable-simple"> <!-- Adjust the margin-right here -->
            <router-link v-if="item.name.length < 86" class="text-decoration-none" :to="{ name: 'processEdit', params: { id: item.id } }">
              <span class="font-weight-regular color-primary">{{ item.name }}</span>
            </router-link>
            <v-tooltip v-else top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <router-link
                    class="text-decoration-none"
                    :to="{ name: 'processEdit', params: { id: item.id } }"
                  >
                    <span class="font-weight-regular color-primary">{{ item.name.substring(0, 62) }}...</span>
                  </router-link>
                </div>
              </template>
              <span>{{ item.name }}</span>
            </v-tooltip>
          </div>
          <!-- 3 dots menu on the right -->
          <div>
            <v-menu class="custom-edit-menu" offset-y nudge-left="75" transition="slide-y-transition"> <!-- Adjust nudge-left if necessary -->
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  variant="text"
                  icon
                  v-on="on"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <!-- Menu with actions -->
              <v-card class="pr-2 pl-1" style="border: #CBD5E1 1px solid;">
                <v-list dense>
                  <!-- Deactivate/Activate button -->
                  <v-list-item
                    :disabled="item.isSystem"
                    link
                    class="text--secondary font-weight-light"
                    @click="$emit('changeStatus', {id: item.id, status: item.status})"
                  >
                    {{ item.status === 'ACTIVE' ? $lang.actions.deactivate : $lang.actions.activate }}
                  </v-list-item>

                  <!-- Copy Create button -->
                  <v-list-item
                    v-if="roles.includes('PROCESS_CREATOR') || roles.includes('SUPER_USER')"
                    link
                    class="text--secondary font-weight-light"
                    @click="$emit('copyCreate', item)"
                  >
                    {{ $lang.actions.copyCreate }}
                  </v-list-item>

                  <v-divider class="my-1" />

                  <!-- Open associated StepLog -->
                  <v-list-item
                    link
                    data-cy="open-process-events clickable-simple"
                    class="text--secondary font-weight-light"
                    @click="openStepLogs(item.id)"
                  >
                    {{ $lang.labels.stepLogs }}
                  </v-list-item>

                  <!-- Open associated Event -->
                  <v-list-item
                    link
                    data-cy="open-process-events"
                    class="text--secondary font-weight-light clickable-simple"
                    @click="openProcessEvents(item.id)"
                  >
                    {{ $lang.labels.openProcessEvents }}
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </div>
      </template>

      <template v-slot:item.createdOn="{ item }">
        <div>{{ item.createdOn | formatDateTime }}</div>
      </template>

      <template v-slot:item.modifiedOn="{ item }">
        <div>{{ item.modifiedOn | formatDateTime }}</div>
      </template>

      <template v-slot:item.status="{ item }">
        <div class="custom-active-deactive align-center">
          <p :class="{'custom-active': item.status === 'ACTIVE', 'custom-deactivated': item.status !== 'ACTIVE', 'm-0 p-0 mb-0': true }">
            {{ $lang.status[item.status] }}
          </p>
        </div>
      </template>

      <template v-slot:item.isSystem="{ item }">
        <div>{{ item.isSystem ? $lang.status.YES : $lang.status.NO }}</div>
      </template>

      <template v-slot:item.comment="{ item }">
        <div style="white-space: nowrap;overflow: hidden;max-width: 300px;text-overflow: ellipsis">{{ item.comment }}</div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
// import editAccent from '@/icons/edit-accent.svg'
import { mapState } from 'vuex'
import { bus } from '@/main'
import { format, sub } from 'date-fns'
// import {
//   getEventByIdUsingGET as getEvent,
//   getProcessStepLogsUsingGET as getLogs
// } from '@/utils/api'

export default {
  props: {
    items: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showImport: false,
      lock: true,
      activeOnly: true,
      includeSystem: false,
      search: '',
      permissionType: '',
      options: {},
      totalItems: 0,
      headers: [
        {
          text: this.$lang.labels.name,
          align: 'start',
          sortable: true,
          value: 'name'
        },
        // { text: this.$lang.labels.actions, value: 'actions', sortable: false },
        { text: this.$lang.labels.isSystem, value: 'isSystem', sortable: true },
        { text: this.$lang.labels.status, value: 'status', sortable: true }

      ],
      roles: [],
      permissionTypes: ['EDIT', 'USE', 'VIEW']
    }
  },
  computed: {
    ...mapState('app', ['rowsPerPageItemsGlobal', 'userRoles', 'userSettings']),
    typesFormatted () {
      return this.permissionTypes.map((item) => {
        return { text: this.$lang.status[item], value: item }
      })
    },
    notChromeBased() {
      return /chrome/i.test(navigator.userAgent) && /google/i.test(navigator.vendor)
    }
  },
  watch: {
    options: {
      handler () {
        if (!this.lock) this.$emit('fetchProcesses', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType })
        this.savePreFill()
      },
      deep: true
    },
    search: {
      handler (val) {
        if (!this.lock && val && val.length > 1) this.$emit('fetchProcesses', { options: this.options, search: val, permissionType: this.permissionType, resetPage: true })
        if (!this.lock && val === null || val === '') this.$emit('fetchProcesses', { options: this.options, search: '', permissionType: this.permissionType, resetPage: true })
        this.savePreFill()
      }
    },
    activeOnly: {
      handler (val) {
        if (!this.lock) this.$emit('activeOnly', { activeOnly: val, includeSystem: this.includeSystem, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true } })
        this.savePreFill()
      }
    },
    includeSystem: {
      handler (val) {
        if (!this.lock) this.$emit('includeSystem', { includeSystem: val, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true } })
        this.savePreFill()
      }
    },
    permissionType: {
      handler () {
        if (!this.lock) this.$emit('fetchProcesses', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
        this.savePreFill()
      }
    }
  },
  mounted() {
    bus.$on('refreshData', (name) => {
      if (!this.lock && name === this.$route.name) this.$emit('fetchProcesses', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
    })
  },
  created() {
    this.roles = this.userRoles.map((x) => x.name) || []

    if (this.userSettings.display.showComments) {
      this.headers.splice(3, 0, { text: this.$lang.labels.comment, value: 'comment' })
    }

    if (this.userSettings.display.showModifiedOn) {
      this.headers.splice(3, 0, { text: this.$lang.labels.modifiedOn, value: 'modifiedOn' })
    }

    if (this.userSettings.display.showCreatedOn) {
      this.headers.splice(3, 0, { text: this.$lang.labels.createdOn, value: 'createdOn', class: 'created-on' })
    }

    if (this.userSettings.display.showId) {
      this.headers.splice(0, 0, { text: this.$lang.header.id, value: 'id' })
    }

    if (localStorage.preFillData) {
      const preFill = JSON.parse(localStorage.preFillData)

      if (preFill && preFill.processList) {
        this.search = preFill.processList.search
        this.activeOnly = preFill.processList.activeOnly
        this.includeSystem = preFill.processList.includeSystem
        this.permissionType = preFill.processList.permissionType
        this.options = preFill.processList.options

        this.$emit('activeOnly', { activeOnly: this.activeOnly, includeSystem: this.includeSystem, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true } })
      } else {
        this.$emit('fetchProcesses', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
      }
    } else {
      this.$emit('fetchProcesses', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', permissionType: this.permissionType, resetPage: true })
    }
    setTimeout(() => this.lock = false, 100)
  },
  methods: {
    openStepLogs(processId) {
      const dateFrom = format(sub(new Date(), { days: 1 }), 'yyyy-MM-dd')
      const dateTill = format(new Date(), 'yyyy-MM-dd')

      const url = this.$router.resolve({
        name: 'logs-steps',
        query: { processId: String(processId), dateFrom, dateTill }
      })

      window.open(url.href, '_blank')
    },
    openProcessEvents(processId) {
      const dateFrom = format(sub(new Date(), { days: 1 }), 'yyyy-MM-dd')
      const dateTill = format(new Date(), 'yyyy-MM-dd')

      const url = this.$router.resolve({
        name: 'logs-events',
        query: { processId: String(processId), dateFrom, dateTill }
      })

      window.open(url.href, '_blank')
    },
    createFromClipboard () {
      this.$router.push({ name: 'processCreate', params: { copyFromMemory: true } })
    },
    savePreFill() {
      if (!localStorage.preFillData) localStorage.preFillData = '{}'
      if (localStorage.preFillData) {
        const preFill = JSON.parse(localStorage.preFillData)

        if (!preFill.processList) preFill.processList = {
          search: '',
          activeOnly: true,
          includeSystem: false,
          permissionType: '',
          options: {
            page: 1,
            itemsPerPage: 25,
            sortBy: [],
            sortDesc: []
          }
        }

        preFill.processList.search = this.search
        preFill.processList.activeOnly = this.activeOnly
        preFill.processList.includeSystem = this.includeSystem
        preFill.processList.permissionType = this.permissionType
        preFill.processList.options = { ...this.options, page: 1 }

        localStorage.preFillData = JSON.stringify(preFill)
      }
    }
  }
}
</script>
